import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'

import { OutputType } from 'app/constants/outputType'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { GroupModel } from 'app/models/groupModel'
import { UserModel } from 'app/models/userModel'

import { AdminIcon, AdminDateField, CheckboxField } from '@/modules/shared/components'
import { getCompanyTypeIcon } from 'app/services/company/companyIconResolver'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminModal, AdminModalRow } from '../shared/AdminModal'

const SORT_BY_OPTIONS = [
  { name: 'audit-report.sort-by-company', type: 0 },
  { name: 'audit-report.sort-by-user', type: 1 },
  { name: 'audit-report.sort-by-event', type: 2 },
  { name: 'audit-report.sort-by-date', type: 3 },
]

const EVENTS_1 = [
  'login',
  'loginIntoCompany',
  'importFile',
  'calculateTimesheet',
  'generateReport',
  'activateDrivers',
  'deactivateDrivers',
  'deleteDrivers',
]

const EVENTS_2 = [
  'generateAttestation',
  'addActivity',
  'editActivity',
  'saveContract',
  'deleteContract',
  'createBusinessTrip',
  'editBusinessTrip',
  'deleteBusinessTrip',
]

const ALL_EVENTS = [...EVENTS_1, ...EVENTS_2]

/** `importFile` obejmuje na serwerze kilka podtypów - stara wersja dokładała je ręcznie. */
const collectEvents = (selected: string[]) =>
  selected.includes('importFile')
    ? [
        ...selected,
        'importUnknownFile',
        'importDddFile',
        'importGpsFile',
        'importActivityFile',
        'importAbsencesFile',
      ]
    : selected

type Props = {
  currentCompanyId: string
  companies: CompanyDetailsModel[]
  users: UserModel[]
  groups: GroupModel[]
  getCompanies: (
    tenantId?: string,
    includeDeleted?: boolean,
    includeChildren?: boolean,
  ) => Promise<Array<CompanyDetailsModel>>
  getGroups: (tenantId?: string) => Promise<Array<GroupModel>>
  getAvailableUsers: (companyId?: string) => Promise<Array<UserModel>>
  getAuditReport: (
    companies: Array<string>,
    groups: Array<string>,
    user: string,
    startDate: Moment,
    endDate: Moment,
    events: Array<string>,
    outputType: OutputType,
    sortBy: number,
  ) => any
  onClose: () => void
}

/** Wydruk raportu audytu - funkcyjny odpowiednik `printout/auditModal.tsx`. */
export const AuditModal: React.FC<Props> = ({
  currentCompanyId,
  companies,
  users,
  groups,
  getCompanies,
  getGroups,
  getAvailableUsers,
  getAuditReport,
  onClose,
}) => {
  const { t } = useLocalize()

  const [isCsv, setIsCsv] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState<Moment>(
    moment().add(-1, 'months'),
  )
  const [dateTo, setDateTo] = React.useState<Moment>(moment())
  const [selectedEvents, setSelectedEvents] =
    React.useState<string[]>(ALL_EVENTS)
  const [checkAllEvents, setCheckAllEvents] = React.useState(true)
  const [selectedCompanies, setSelectedCompanies] = React.useState<string[]>([])
  const [selectedGroups, setSelectedGroups] = React.useState<string[]>([])
  const [sortBy, setSortBy] = React.useState(0)
  const [filterByUser, setFilterByUser] = React.useState(false)
  const [selectedUser, setSelectedUser] = React.useState<string>(null)

  React.useEffect(() => {
    // includeChildren: zdarzenia audytu zapisują się na firmie, na której faktycznie
    // pracowano, więc dla konta usługowego niosą id firmy ze scope'u, nie konta.
    getCompanies(undefined, false, true)
    getGroups(currentCompanyId)
    getAvailableUsers(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompanyId])

  const allCompanies = React.useMemo(() => {
    const result: CompanyDetailsModel[] = []
    for (const company of companies) {
      result.push({ ...company })
      for (const child of company.childCompanies ?? []) {
        result.push({ ...child })
      }
    }
    return result
  }, [companies])

  React.useEffect(() => {
    setSelectedCompanies(allCompanies.map((company) => String(company.id)))
  }, [allCompanies])

  const toggleEvent = (event: string) =>
    setSelectedEvents((prev) =>
      prev.includes(event)
        ? prev.filter((item) => item !== event)
        : [...prev, event],
    )

  const renderEvents = (events: string[], group: number) => (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {events.map((event, index) => (
        <CheckboxField
          key={event}
          id={`event_${group}_${index}`}
          checked={selectedEvents.includes(event)}
          label={t(`audit-report.${event}`)}
          onChange={() => toggleEvent(event)}
        />
      ))}
    </div>
  )

  return (
    <AdminModal
      title={t('audit-report.generate-audit-report-title')}
      width={760}
      onClose={onClose}
      onConfirm={() => {
        onClose()
        getAuditReport(
          selectedCompanies,
          selectedGroups,
          selectedUser,
          dateFrom,
          dateTo,
          collectEvents(selectedEvents),
          isCsv ? OutputType.Csv : OutputType.Pdf,
          sortBy,
        )
      }}
    >
      <AdminModalRow label={t('audit-report.period')}>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-w-[190px]">
            <AdminDateField
              withTime
              value={dateFrom}
              onChange={(date) => date && setDateFrom(moment(date))}
            />
          </div>
          <span>-</span>
          <div className="tw-w-[190px]">
            <AdminDateField
              withTime
              value={dateTo}
              onChange={(date) => date && setDateTo(moment(date))}
            />
          </div>
        </div>
      </AdminModalRow>

      <AdminModalRow label={t('audit-report.companies')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          searchPlaceholder={t(
            'administrationPanel-users.filter-find-placeholder',
          )}
          placeholder={t('administrationPanel-users.filter-companies')}
          options={allCompanies.map((company) => ({
            value: String(company.id),
            label: company.companyName,
            content: (
              <span className="tw-flex tw-items-center tw-gap-2">
                <AdminIcon
                  name={getCompanyTypeIcon(
                    company.type,
                    company.accountingOnly,
                  )}
                  className="tw-h-4 tw-w-4"
                />
                <span className="tw-flex tw-flex-col">
                  <span>{company.companyName}</span>
                  {company.tenant != null && (
                    <span className="tw-text-xs tw-opacity-70">
                      {company.tenant.companyName}
                    </span>
                  )}
                </span>
              </span>
            ),
          }))}
          value={selectedCompanies}
          onChange={setSelectedCompanies}
        />
      </AdminModalRow>

      <AdminModalRow label={t('audit-report.groups')}>
        <Dropdown
          compact
          multiple
          showSelectAll
          portalMenu
          placeholder={t('administrationPanel-users.choose-group')}
          options={groups.map((group) => ({
            value: String(group.id),
            label: group.name ?? '',
          }))}
          value={selectedGroups}
          onChange={setSelectedGroups}
        />
      </AdminModalRow>

      <AdminModalRow label={t('audit-report.user')}>
        <div className="tw-flex tw-items-center tw-gap-3">
          <CheckboxField
            id="filterByUser"
            checked={filterByUser}
            label=""
            onChange={() => {
              if (filterByUser) setSelectedUser(null)
              setFilterByUser((value) => !value)
            }}
          />
          <div
            className="tw-min-w-0 tw-flex-1"
            style={{ opacity: filterByUser ? 1 : 0.3 }}
          >
            <Dropdown
              compact
          portalMenu
              disabled={!filterByUser}
              searchPlaceholder={t('administrationPanel-companies.find-user')}
              placeholder={t('administrationPanel-companies.choose-user')}
              // Kluczem jest `id`, nie `login` - ten sam login potrafi się powtórzyć
              // w liście (React zgłaszał wtedy zduplikowane klucze).
              options={(users || []).map((user) => ({
                value: String(user.id),
                label: `${user.firstName || '-'} ${user.surname || '-'}, ${user.login}`,
                content: (
                  <span className="tw-flex tw-flex-col">
                    <span className="tw-text-primary">{user.login}</span>
                    <span>{`${user.firstName || '-'} ${user.surname || '-'}`}</span>
                  </span>
                ),
              }))}
              value={
                (users || [])
                  .find((user) => user.login === selectedUser)
                  ?.id?.toString() ?? null
              }
              onChange={(id: string) =>
                setSelectedUser(
                  (users || []).find((user) => String(user.id) === id)?.login,
                )
              }
            />
          </div>
        </div>
      </AdminModalRow>

      <AdminModalRow label={t('audit-report.sort-by')}>
        <Dropdown
          compact
          portalMenu
          value={String(sortBy)}
          options={SORT_BY_OPTIONS.map((option) => ({
            value: String(option.type),
            label: t(option.name),
          }))}
          onChange={(value) => setSortBy(Number(value))}
        />
      </AdminModalRow>

      <div className="tw-mb-2 tw-flex tw-items-center tw-gap-4">
        <span className="tw-w-40 tw-flex-shrink-0 tw-text-sm tw-text-content-base">
          {t('audit-report.events')}
        </span>
        <CheckboxField
          id="checkAllEvents"
          checked={checkAllEvents}
          label={t('audit-report.checkAll')}
          onChange={() => {
            const negation = !checkAllEvents
            setSelectedEvents(negation ? ALL_EVENTS : [])
            setCheckAllEvents(negation)
          }}
        />
      </div>
      <div className="tw-flex tw-gap-12 tw-pl-44">
        {renderEvents(EVENTS_1, 1)}
        {renderEvents(EVENTS_2, 2)}
      </div>

      <div className="tw-mt-4">
        <CheckboxField
          id="isCsv"
          checked={isCsv}
          label={t('audit-report.isCsv')}
          onChange={() => setIsCsv((value) => !value)}
        />
      </div>
    </AdminModal>
  )
}
